import { KeyRound } from "lucide-react";
import { safeServices, safeStatement, safeHighlights, reviews } from "@/data/siteConfig";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { SectionHeader } from "@/components/SectionHeader";
import { ReviewCard } from "@/components/ReviewCard";
import { CallButton } from "@/components/CallButton";
import { ContactCTA } from "@/components/home/ContactCTA";
import { RevealGroup, RevealItem, Reveal } from "@/components/Reveal";

export default function SafeServices() {
  return (
    <>
      <Seo
        title="Safe Services — Opening, Moving & Repair"
        description="Velasco Lock and Safe specializes in all types of safes — safe opening, heavy safe moving, removal, installation and lock/keypad repair throughout Southern California. Difficult safe jobs welcome. Call (562) 716-4447."
        path="/safe-services"
      />
      <PageHero
        overline="Safe Services"
        title="All types of safes. Even the difficult ones."
        description="Safe opening, moving, heavy safe moving, removal, disposal, installation, lock repair and retrofits — including oversized, high-security safes and vault work that some companies won't take on."
      />

      {/* statement + stats */}
      <section className="grain relative bg-ink py-20 md:py-24">
        <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-8">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <p className="font-heading text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl">
              {safeStatement}
            </p>
            <div className="grid grid-cols-2 gap-px self-start overflow-hidden border border-white/10 bg-white/10">
              {safeHighlights.map((s) => (
                <div key={s.label} className="bg-ink p-6">
                  <div className="font-heading text-3xl font-black text-gold">{s.value}</div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-steel">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* service grid */}
      <section className="border-y border-white/10 bg-ink-surface py-24 md:py-28">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <SectionHeader overline="Capabilities" title="What we handle" />
          <RevealGroup className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {safeServices.map((s, i) => {
              const Icon = s.icon;
              return (
                <RevealItem key={s.slug}>
                  <div
                    data-testid={`safe-detail-${s.slug}`}
                    className="group flex h-full flex-col border border-white/10 bg-ink p-7 transition-colors duration-300 hover:border-gold/40"
                  >
                    <div className="mb-5 flex items-center justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-sm border border-white/10 bg-ink-surface text-gold transition-colors duration-300 group-hover:border-gold/50">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="font-heading text-xs font-bold text-white/15">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-white">{s.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-steel">{s.body}</p>
                  </div>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* difficult access highlight */}
      <section className="bg-ink py-20 md:py-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <Reveal>
            <div className="relative overflow-hidden border border-gold/25 bg-gradient-to-br from-ink-surface to-ink p-8 text-center md:p-16">
              <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-gold/10 blur-[110px]" />
              <div className="relative z-10 mx-auto max-w-2xl">
                <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-sm border border-gold/40 bg-gold/10 text-gold">
                  <KeyRound className="h-6 w-6" />
                </span>
                <h3 className="font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                  Turned away elsewhere? Call us.
                </h3>
                <p className="mt-4 leading-relaxed text-steel">
                  Jose regularly takes on difficult, oversized and specialized safe jobs.
                  Because every safe is different, he'll evaluate yours before any work
                  begins — no unverifiable promises, just honest expertise.
                </p>
                <div className="mt-8 flex justify-center">
                  <CallButton size="lg" showNumber testid="safe-highlight-call-btn" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* safe reviews */}
      <section className="border-t border-white/10 bg-ink-surface py-24 md:py-28">
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <SectionHeader overline="Proof" title="Safe work customers remember" />
          <RevealGroup className="mt-14 grid gap-6 md:grid-cols-3">
            {reviews.slice(3, 6).map((r, i) => (
              <RevealItem key={i}>
                <ReviewCard review={r} />
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <ContactCTA />
    </>
  );
}
